<script setup lang="ts">
    import { type TemplateTyping } from '@/typings/widget';
    import { DateTime } from '@shared/helpers';

    const props = defineProps<{
        template: TemplateTyping
        selected?: boolean
    }>();

    const templateDays = computed(() => props.template.programme.map(day => day.timeslots?.map(timeslot => ({
        start: DateTime.dayjsObject(timeslot.startDateTime)
            .format('HH:mm'),
        end: timeslot.endDateTime
            ? DateTime.dayjsObject(timeslot.endDateTime)
                .format('HH:mm')
            : '',
        name: timeslot.name,
    }))));
</script>

<template>
    <div
        :class="{'template-item--selected': selected}"
        class="template-item"
    >
        <span class="template-item__title">{{ template.name }}</span>
        <div class="template-item__days">
            <div
                v-for="(day, dayIndex) in templateDays"
                :key="`${dayIndex}-day`"
                class="template-item__day"
            >
                <div
                    v-for="(timeslot) in day"
                    :key="timeslot.name"
                    class="template-item__timeslot"
                >
                    <div class="template-item__timeslot__time">
                        <span>
                            {{ timeslot.start }}
                        </span>
                        <template v-if="timeslot.end">
                            -
                            <span>
                                {{ timeslot.end }}
                            </span>
                        </template>
                    </div>
                    <span>
                        {{ timeslot.name }}
                    </span>
                </div>
            </div>
        </div>
    </div>
</template>

<style lang="scss" scoped>
.template-item {
    display: flex;
    flex-direction: column;
    padding: var(--spacing-04);
    border: 1px solid var(--border-white);
    border-radius: var(--border-radius-md);
    cursor: pointer;
    gap: var(--spacing-05);

    &:hover,
    &--selected {
        background: var(--background-secondary-highlight);
    }

    &__title {
        font-size: var(--font-medium);
        font-weight: var(--font-weight-bold);
    }

    &__days,
    &__day,
    &__timeslot {
        display:flex;
    }

    &__days,
    &__day {
        flex-direction: column;
    }

    &__days {
        gap: var(--spacing-04);
    }

    &__day {
        gap: var(--spacing-01);
    }

    &__timeslot {
        display: flex;
        justify-content: space-between;
        gap: var(--spacing-02);

        > span {
            overflow: hidden;
            flex: 1 1 auto; // allow it to grow/shrink based on space
            font-size: var(--font-regular);
            min-inline-size: 0;   // necessary to allow text-overflow to work in flexbox
            text-overflow: ellipsis;
            white-space: nowrap;
        }

        &__time {
            display: flex;
            flex-shrink: 0;     // prevent it from shrinking
            align-items: center;
            gap: var(--spacing-01);
            inline-size: 100px;
            letter-spacing: 0.5px;

            span {
                font-size: var(--font-small);
                font-weight: var(--font-weight-light);
                inline-size: var(--size-6);
                letter-spacing: var(--letter-spacing-lg);
                line-height: 1;
                text-align: center;

                &:last-child {
                    padding-inline-start: 2px;
                }
            }
        }
    }
}
</style>
